import React, { useEffect, useState } from 'react';
import { Table, Button, Spinner, Modal, Row, Col, Badge } from 'react-bootstrap';
import api from '../../../api/apiClient';

const MyTransactionsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const response = await api.payments.listMy();
        if (response.data?.success && Array.isArray(response.data.data)) {
          setPayments(response.data.data);
        } else if (Array.isArray(response.data)) {
          setPayments(response.data);
        } else {
          setPayments([]);
        }
      } catch (error) {
        console.error('Error loading transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const handleViewInvoice = (payment) => {
    setSelectedPayment(payment);
    setShowInvoice(true);
  };

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    setSelectedPayment(null);
  };

  const formatAmount = (value) => {
    if (value == null) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return num.toFixed(2);
  };

  const renderStatusBadge = (status) => {
    const normalized = (status || '').toLowerCase();
    let gradient = 'from-gray-500 to-gray-600';
    if (normalized === 'success') gradient = 'from-emerald-500 to-teal-600';
    else if (normalized === 'failed') gradient = 'from-red-500 to-rose-600';
    else if (normalized === 'pending') gradient = 'from-amber-500 to-yellow-600';
    return (
      <Badge className={`bg-gradient-to-r ${gradient} text-white px-3 py-1 rounded-full font-semibold text-uppercase`}>
        {status}
      </Badge>
    );
  };

  const renderInvoiceBody = () => {
    if (!selectedPayment) return null;
    const meta = selectedPayment.metadata || {};
    const items = Array.isArray(meta.originalCartSnapshot)
      ? meta.originalCartSnapshot
      : Array.isArray(meta.cartSnapshot)
        ? meta.cartSnapshot
        : [];

    return (
      <>
        <Row className="mb-3">
          <Col md={6}>
            <div className="small text-muted">Invoice #</div>
            <div className="fw-semibold">PAY-{selectedPayment.id}</div>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <div className="small text-muted">Date</div>
            <div className="fw-semibold">{new Date(selectedPayment.createdAt).toLocaleString()}</div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <div className="small text-muted">Payment Method</div>
            <div className="fw-semibold text-capitalize">{selectedPayment.payment_type}</div>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <div className="small text-muted">Transaction ID</div>
            <div className="fw-semibold">{selectedPayment.transaction_id || '-'}</div>
          </Col>
        </Row>

        <div className="table-responsive mb-3">
          <Table size="sm" bordered hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Course</th>
                <th className="text-end">Price</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-3">
                    No course details available.
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item.courseName || `Course ${item.courseId}`}</td>
                    <td className="text-end">{formatAmount(item.price)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <Row className="justify-content-end">
          <Col md={6} lg={4}>
            <Table borderless size="sm" className="mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Subtotal</td>
                  <td className="text-end fw-semibold">
                    {formatAmount(selectedPayment.subtotal_amount ?? meta.subtotalAmount ?? selectedPayment.amount)}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Voucher Discount</td>
                  <td className="text-end fw-semibold text-danger">
                    {selectedPayment.voucher_amount || meta.voucherDiscountAmount
                      ? `- ${formatAmount(selectedPayment.voucher_amount ?? meta.voucherDiscountAmount)}`
                      : '-'}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Final Amount</td>
                  <td className="text-end fw-bold">
                    {formatAmount(selectedPayment.final_amount ?? meta.finalAmount ?? selectedPayment.amount)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-5 py-4 sm:p-6 border border-purple-100 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl shadow-md">
              <i className="fas fa-receipt"></i>
            </div>
            <div>
              <h2 className="text-left text-2xl sm:text-2xl font-bold text-gray-900 mb-1">My Transactions</h2>
              <p className="text-gray-600 text-sm sm:text-base m-0">
                View your course purchase history and invoices
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-purple-100 shadow-xl p-4 sm:p-6">
        {loading ? (
          <div className="text-center py-12 sm:py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-purple-600 border-t-transparent mb-4"></div>
            <p className="text-gray-600 text-base sm:text-lg font-medium">Loading transactions...</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <i className="fas fa-receipt text-white text-3xl sm:text-3xl"></i>
            </div>
            <h5 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No transactions found</h5>
            <p className="text-gray-600 text-base sm:text-lg">Your transaction history will appear here once you make a purchase.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table hover responsive className="mb-0">
              <thead>
                <tr className="border-bottom-2">
                  <th className="fw-bold text-gray-700 py-3">Date</th>
                  <th className="fw-bold text-gray-700 py-3">Payment ID</th>
                  <th className="fw-bold text-gray-700 py-3">Provider</th>
                  <th className="fw-bold text-gray-700 py-3">Status</th>
                  <th className="fw-bold text-gray-700 py-3 text-end">Subtotal</th>
                  <th className="fw-bold text-gray-700 py-3 text-end">Discount</th>
                  <th className="fw-bold text-gray-700 py-3 text-end">Final Amount</th>
                  <th className="fw-bold text-gray-700 py-3">Transaction ID</th>
                  <th className="fw-bold text-gray-700 py-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-bottom">
                    <td className="py-3 text-gray-700">{payment.createdAt ? new Date(payment.createdAt).toLocaleString() : '-'}</td>
                    <td className="py-3">
                      <code className="bg-purple-50 text-purple-700 px-2 py-1 rounded font-semibold">PAY-{payment.id}</code>
                    </td>
                    <td className="py-3 text-capitalize text-gray-700">{payment.payment_type}</td>
                    <td className="py-3">{renderStatusBadge(payment.status)}</td>
                    <td className="py-3 text-end fw-semibold text-gray-900">₹{formatAmount(payment.subtotal_amount ?? payment.amount)}</td>
                    <td className="py-3 text-end text-danger fw-semibold">
                      {payment.voucher_amount ? `-₹${formatAmount(payment.voucher_amount)}` : '-'}
                    </td>
                    <td className="py-3 text-end fw-bold text-purple-600">₹{formatAmount(payment.final_amount ?? payment.amount)}</td>
                    <td className="py-3">
                      <code className="bg-gray-50 text-gray-700 px-2 py-1 rounded text-sm">{payment.transaction_id || '-'}</code>
                    </td>
                    <td className="py-3 text-end">
                      <Button
                        className="border-0 text-white py-1 px-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 font-semibold"
                        style={{ 
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                          borderRadius: '8px'
                        }}
                        size="sm"
                        onClick={() => handleViewInvoice(payment)}
                      >
                        View Invoice
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={showInvoice} onHide={handleCloseInvoice} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-gray-900">Invoice Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">{renderInvoiceBody()}</Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button 
                        bsPrefix="custom-hamburger"
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
        
            onClick={handleCloseInvoice}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MyTransactionsPage;
